/**
 * Меню за шаблоном WAI-ARIA Menu Button (components.md, розд. 8): ↑ ↓ Home End, Enter/Space
 * обирає, Esc закриває й повертає фокус на кнопку, клік поза меню закриває. Місце, `Esc` і «клік
 * поза» дає примітив шару `<Popover>` (розд. 8a): меню під кнопкою чи над нею, у межах вікна.
 * Свій мінімум замість React Aria: висновок spike — розд. «Що вклалося». Пункти, клавіші й фокус
 * відкритого меню — `MenuList.tsx`, у лінивому модулі шарів (`Popover.tsx`, «Лінива поведінка»).
 */
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { MenuItem } from './layer-types.ts';
import { useLayerModule, usePendingClose } from './Popover.tsx';

export type { MenuItem };

interface MenuButtonProps {
  label: string;
  items: MenuItem[];
  className?: string;
  children: ReactNode;
  align?: 'start' | 'end';
  /** Відкрити ззовні (клавіша `S` на задачі). */
  openSignal?: number | undefined;
  /** Підказка кнопки з клавішею («Подзвонити (C)»): клавіша — тут, а не `kbd` у вузькій шапці. */
  title?: string | undefined;
  /** Клавіша кнопки для програми читання (`aria-keyshortcuts`). */
  keyshortcuts?: string | undefined;
  /** `-1` — поза порядком `Tab` (roving tabindex дошки: кнопки неактивної картки). */
  tabIndex?: number | undefined;
  /** Меню відкрилось: пункти, яким потрібні дані, вантажать їх лише тепер («Написати ▾»). */
  onOpen?: (() => void) | undefined;
}

export function MenuButton({
  label,
  items,
  className,
  children,
  align = 'end',
  openSignal,
  title,
  keyshortcuts,
  tabIndex,
  onOpen,
}: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  // Відкрите меню — лінивий модуль шарів: прийде раніше за перше відкриття (простій після першого
  // екрана), а відкрите раніше — з'явиться, щойно модуль прийде (фокус — на першому пункті, якщо
  // людина його нікуди не перенесла). До модуля `Esc` і клік поза вже закривають меню; збій — теж.
  const layers = useLayerModule(open, () => setOpen(false));
  usePendingClose(open && !layers, buttonRef, null, () => setOpen(false));
  // Де був фокус, коли меню відкрили до приходу модуля (`null` — модуль уже був).
  const waitedFrom = useRef<Element | null>(null);
  useLayoutEffect(() => {
    if (!open) waitedFrom.current = null;
    else if (!layers) waitedFrom.current = document.activeElement;
  }, [open, layers]);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: сигнал — саме відкриття, не новий обробник
  useEffect(() => {
    if (open) onOpen?.();
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        aria-label={label}
        title={title}
        aria-keyshortcuts={keyshortcuts}
        tabIndex={tabIndex}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open && layers ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {children}
      </button>
      {open && layers && (
        <layers.MenuList
          id={menuId}
          label={label}
          items={items}
          align={align}
          buttonRef={buttonRef}
          waitedFrom={waitedFrom}
          close={close}
        />
      )}
    </>
  );
}
